/**
 * Which service screen a store sees: the design system's (`system`) or the one production has painted
 * until now (`current`). The critical lane — the header, the order list, the open order with its three
 * actions and the verdict — moves to the system store by store, behind PostHog's `ui-system-lane` flag,
 * assigned per `restaurant_id` like `photo-coverage-alerts` (norm 13; Adrián, 8-oct-2026).
 *
 * public/index.html sets `data-bz-lane` on <html> before the first paint, from what this module stored at
 * the last boot. The lane's components key their new styles on `:root[data-bz-lane="system"]` and their
 * new markup on `systemLane()`, and leave production's styles untouched beside them: turning the flag off
 * is the rollback, and gives back production's service screen. What the flag does NOT cover went to the
 * whole fleet on purpose (docs/sistema-diseno/etapas.md, «Toda la app al sistema»): the deliberate
 * screens, the toasts, the banner and the timeout, and step 1 of forma-y-tamano.md (no pulses, no stuck
 * hover, accessible names, free zoom).
 *
 * @rule   A change of the flag is stored for the NEXT boot, never applied to the open page. Unlike the
 *         `ui-v2` palette (src/ui/palette.js), this one is visible: the cards and the buttons change shape,
 *         and nothing may change under a cook's finger in the middle of service. A tablet picks it up when
 *         the app next loads (the reload button, an update, a restart), which is at most the flag's own
 *         propagation time anyway (norm 13: up to 6 h).
 * @rule   Without PostHog (it failed to start) the flag is never read again and the last stored value stays:
 *         a store keeps the lane it had, as with any flag of this app.
 * @rule   Two overrides, for trying it without the flag: `?lane=system|current` for the tab's session (the
 *         visual harness and the e2e suite), and Debug's «Pantalla de servicio» for this tablet, kept in
 *         localStorage until changed. The override wins over the flag — except in production, where
 *         neither exists and only the flag decides (src/devTools.js, 15-oct-2026).
 */
export const LANE_FLAG = 'ui-system-lane';
/** What the flag said at the last boot. */
export const LANE_KEY = 'bz-lane-flag';
/** This tablet's own choice from Debug: `system`, `current`, or nothing (the store's flag decides). */
export const LANE_OVERRIDE_KEY = 'bz-lane-override';
/** The tab's choice from `?lane=`. */
export const LANE_SESSION_KEY = 'bz-lane';

export const LANES = ['system', 'current'];

/** The lane the flag gives: `system` only when it is on, so a missing or unknown flag is `current`. */
export const laneFor = (enabled) => (enabled === true ? 'system' : 'current');

/** The lane this page was painted with: what public/index.html decided before the first frame. */
export function currentLane(root = typeof document !== 'undefined' ? document.documentElement : null) {
    return root && root.getAttribute('data-bz-lane') === 'system' ? 'system' : 'current';
}

/** True when this page paints the critical lane on the design system. Fixed for the page's life. */
export const systemLane = (root) => currentLane(root) === 'system';

/** This tablet's override from Debug, or '' when the store's flag decides. */
export function laneOverride(local) {
    try {
        const value = (local ?? window.localStorage).getItem(LANE_OVERRIDE_KEY);
        return LANES.includes(value) ? value : '';
    } catch {
        return '';
    }
}

/** Sets (or, with '', clears) this tablet's override. It takes effect on the next load. */
export function setLaneOverride(value, local) {
    try {
        const storage = local ?? window.localStorage;
        if (LANES.includes(value)) storage.setItem(LANE_OVERRIDE_KEY, value);
        else storage.removeItem(LANE_OVERRIDE_KEY);
    } catch { /* no storage: the store's flag keeps deciding */ }
}

/**
 * Keeps the stored flag current, for the next boot. Never touches the open page (see the header).
 *
 * @param {{ onFeatureFlags: Function, isFeatureEnabled: Function }} posthog
 * @param {{ local?: Storage }} [env]
 */
export function syncLaneFlag(posthog, env = {}) {
    return posthog.onFeatureFlags((flags, variants, context) => {
        // Flags that failed to load say nothing about the store: keep what the last boot stored.
        if (context?.errorsLoading) return;
        const lane = laneFor(posthog.isFeatureEnabled(LANE_FLAG));
        try { (env.local ?? window.localStorage).setItem(LANE_KEY, lane); } catch { /* the next boot paints `current` */ }
    });
}
